import {
  getClient,
  type Client,
  type Components,
  type EntityResponseGroupedWithHits,
  type EntityResponseWithHits,
  type EntitySearchParams,
  type EntitySlug
} from '@epilot/customer-portal-client'

import { previewProxyAdapter } from 'dummy/proxy-adapter'
import AuthUtils from 'utils/AuthUtils'
import { getCurrentLanguage } from 'utils/LanguageUtils'

import { getConfig } from '../EnvConfig'

let customerApiClient: Client | null = null

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let originalAdapter: any = null

// Resolves the browser's IANA timezone for the x-time-zone request header.
// The portal API uses this to render UTC system attributes (e.g.
// {{opportunity._created_at}}) in the viewer's local time instead of UTC.
const getBrowserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Berlin'
  } catch {
    return 'Europe/Berlin'
  }
}

export const getCustomerApiClient = () => {
  if (customerApiClient) return customerApiClient

  customerApiClient = getClient()

  customerApiClient.defaults.baseURL = getConfig().CUSTOMER_PORTAL_API_URL

  if (customerApiClient.interceptors.request['handlers'].length > 3) {
    console.error(
      'Sanity check: This client already has more than 3 request interceptors, refusing to add more!',
      {
        baseURL: customerApiClient.defaults.baseURL,
        interceptors: customerApiClient.interceptors
      }
    )
  } else {
    customerApiClient.interceptors.request.use(async (request) => {
      const token: string = await AuthUtils.getAccessToken()

      const isNotPublicRequest = !request.url.includes('/public')

      if (token && isNotPublicRequest) {
        request.headers['authorization'] = `Bearer ${token}`
      }

      request.headers['Accept-Language'] = getCurrentLanguage()
      request.headers['x-time-zone'] = getBrowserTimeZone()

      return request
    })
  }

  return customerApiClient
}

/**
 * Install the preview proxy adapter on the customer API client.
 * All requests will be routed through the builder's caching proxy via postMessage.
 */
export function installPreviewProxyAdapter(): void {
  if (originalAdapter !== null) return // already installed

  const client = getCustomerApiClient()

  originalAdapter = client.defaults.adapter
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client.defaults.adapter = previewProxyAdapter as any
}

/**
 * Remove the preview proxy adapter and restore the original adapter.
 */
export function removePreviewProxyAdapter(): void {
  if (originalAdapter === null) return

  const client = getCustomerApiClient()

  client.defaults.adapter = originalAdapter
  originalAdapter = null
}

export interface FilePreviewResult {
  kind: 'pdf' | 'image' | 'unsupported'
  url?: string
  download_url?: string
}

export const getFilePreview = async (
  id: string
): Promise<FilePreviewResult> => {
  const client = getCustomerApiClient()
  const { data } = await client.get<FilePreviewResult>(
    `/v2/portal/user/file/${encodeURIComponent(id)}/preview`
  )

  return data
}

export const PORTAL_TYPES = {
  endCustomerPortal: 'END_CUSTOMER_PORTAL',
  installerPortal: 'INSTALLER_PORTAL'
}

export type {
  Components,
  EntityResponseGroupedWithHits,
  EntityResponseWithHits,
  EntitySearchParams,
  EntitySlug
}

export type Entities = EntityResponseWithHits['results']
export type EntitiesHits = EntityResponseWithHits['hits']
export type EntitiesPagination = EntityResponseWithHits['pagination']
export type EntitiesPaginationFrom =
  EntityResponseWithHits['pagination']['from']
export type EntitiesGrouped = EntityResponseGroupedWithHits['groups']
export type EntitiesGroupedHits = EntityResponseGroupedWithHits['hits']
export type EntitiesGroupedPagination =
  EntityResponseGroupedWithHits['groups_pagination']
export type EntitiesGroupedAfterKey =
  EntityResponseGroupedWithHits['groups_pagination']['after_key']
